import { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  Link as MuiLink,
  Stack,
  Typography
} from '@mui/material';
import { RecordContextProvider } from 'react-admin';
import { Link as RouterLink } from 'react-router-dom';
import { ActivityModal } from '../components/ActivityModal.tsx';
import { RelationshipStateChip } from '../relationships/RelationshipStateChip.tsx';
import {
  daysSince,
  fetchCoolingRelationships,
  formatDaysAgo,
  personName,
  type CoolingRelationships
} from '../relationships/relationshipApi.ts';

// Hidden when nothing has gone quiet. A failed load hides it too, the way the
// summary cards drop out.
export const GoneQuietPanel = ({ onFollowUpLogged }: { onFollowUpLogged?: () => void }) => {
  const [cooling, setCooling] = useState<CoolingRelationships | null>(null);
  const [followUpContactId, setFollowUpContactId] = useState<number | null>(null);

  useEffect(() => {
    fetchCoolingRelationships()
      .then(setCooling)
      .catch(() => setCooling(null));
  }, []);

  if (!cooling?.total) return null;

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          {cooling.total === 1
            ? '1 of your relationships has gone quiet'
            : `${cooling.total} of your relationships have gone quiet`}
        </Typography>
        <Stack divider={<Divider />}>
          {cooling.rows.map((row) => (
            <Box
              key={row.contact.id}
              sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1, flexWrap: 'wrap' }}
            >
              <Box sx={{ flex: 1, minWidth: 220 }}>
                <Typography variant="body2">
                  <MuiLink
                    component={RouterLink}
                    to={`/contacts/${row.contact.id}/show`}
                    underline="hover"
                    sx={{ fontWeight: 600 }}
                  >
                    {personName(row.contact)}
                  </MuiLink>
                  {row.contact.companyName ? (
                    <Typography component="span" variant="body2" color="text.secondary">
                      {` · ${row.contact.companyName}`}
                    </Typography>
                  ) : null}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Last exchange {formatDaysAgo(daysSince(row.lastExchangeAt) ?? 0)}, was{' '}
                  {row.wasState.toLowerCase()}
                </Typography>
              </Box>
              <RelationshipStateChip state={row.state} />
              <Button
                size="small"
                variant="outlined"
                onClick={() => setFollowUpContactId(row.contact.id)}
              >
                Log follow-up
              </Button>
            </Box>
          ))}
        </Stack>
        {cooling.total > cooling.rows.length ? (
          <Typography variant="caption" color="text.secondary">
            Showing {cooling.rows.length} of {cooling.total}.
          </Typography>
        ) : null}
      </CardContent>
      {/* ActivityModal logs against the record in context, so give it the row's contact. */}
      <RecordContextProvider
        value={followUpContactId === null ? undefined : { id: followUpContactId }}
      >
        <ActivityModal
          open={followUpContactId !== null}
          setOpen={(open) => {
            if (!open) setFollowUpContactId(null);
          }}
          keyName="contactId"
          resource="contact-activities"
          onSaved={onFollowUpLogged}
        />
      </RecordContextProvider>
    </Card>
  );
};
